// Shared furniture for the KPI pages: data loading, the ready flag, the masthead, the summary line and the value block.
import {
  CSSProperties,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import { useReportReady } from "../../../TeamReport/OwnershipPostGamePage/hooks";
import { blockStyle, GameMeta, GPATH } from "./kpiLib";
import type { KpiResponse, Mark, WindowSummary } from "./types";

/** A multi-game window replaces the postgame furniture: its name and dates in the title, a W-L box for the score. */
export interface WindowHead {
  name: string;
  range: string;
  line: string;
  summary: WindowSummary | null | undefined;
}

export const LOGO = "/images/primary-logo.png";
const DISP = "var(--pr-disp)";

type Load<T> = { data: T | null; error: string | null; loading: boolean };

/** GET a KPI route (scope added by ExplorerAPI). Re-fetches when `path` changes. A new `refresh` on the same path
 * re-fetches in place: the page keeps showing the last data until the new arrives, and a failed poll keeps it. */
export function useKpiData<T = KpiResponse>(
  path: string | null,
  refresh = 0,
): Load<T> {
  const api = useLeagueApi();
  const [state, setState] = useState<Load<T>>({
    data: null,
    error: null,
    loading: !!path,
  });
  const shown = useRef<string | null>(null);
  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    const poll = shown.current === path;
    shown.current = path;
    if (!poll) setState({ data: null, error: null, loading: true });
    api
      .get(path)
      .then(
        (res) =>
          !cancelled &&
          setState({ data: res.data as T, error: null, loading: false }),
      )
      .catch(
        (e) =>
          !cancelled &&
          setState((s) =>
            poll && s.data
              ? s
              : { data: null, error: String(e?.message || e), loading: false },
          ),
      );
    return () => {
      cancelled = true;
    };
  }, [api, path, refresh]);
  return state;
}

const NO_ERRORS: string[] = [];

/**
 * The automation contract (PA1): the page root carries
 *   data-report-ready = "false" (loading) | "true" (data, images, fonts settled) | "pending" (no KPI values for this
 *   game yet: the packet waits) | "error" (the request failed)
 *   data-report-reason = why it is pending; data-report-errors = what failed without blocking (images, timeout).
 * `settling` holds the flag back while the page is still fitting itself to the sheet; `pageErrors` are the page's own
 * (the player page's "overflow": still too tall at its densest step, so the sheet would print clipped).
 */
export function useKpiReady(
  root: RefObject<HTMLElement>,
  load: Load<KpiResponse>,
  settling = false,
  pageErrors: string[] = NO_ERRORS,
): Record<string, string> {
  const pending = load.data?.status?.state === "pending";
  const { ready, errors } = useReportReady(
    root,
    load.loading || !load.data || pending || settling,
    pageErrors,
  );
  if (load.error)
    return { "data-report-ready": "error", "data-report-errors": "request" };
  if (pending)
    return {
      "data-report-ready": "pending",
      "data-report-reason": load.data?.status?.reason || "pending",
    };
  return {
    "data-report-ready": ready ? "true" : "false",
    ...(errors.length ? { "data-report-errors": errors.join(",") } : {}),
  };
}

export function Glyph({ m, size = 9 }: { m: Mark; size?: number }) {
  return (
    <svg
      viewBox="0 0 10 10"
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      <path
        d={GPATH[m]}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The hero encoding: filled green = met, ink outline + × = missed, dashed grey = no value. */
export function ValueBlock({
  m,
  value,
  padding,
  width,
  fontSize,
  glyphSize = 9,
}: {
  m: Mark;
  value: string;
  padding: string;
  width?: string;
  fontSize: number | string;
  glyphSize?: number;
}) {
  return (
    <span style={blockStyle(m, padding, width)}>
      <Glyph m={m} size={glyphSize} />
      <b
        style={{
          fontFamily: DISP,
          fontWeight: 800,
          fontSize,
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </b>
    </span>
  );
}

export function Legend() {
  const leg = (m: Mark): CSSProperties => ({
    ...blockStyle(m, "0", "22px"),
    height: 15,
    justifyContent: "center",
  });
  return (
    <div
      style={{
        marginLeft: "auto",
        display: "flex",
        gap: 12,
        alignItems: "center",
        fontSize: 10,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "#3a3a3a",
        fontWeight: 700,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={leg("met")}>
          <Glyph m="met" />
        </span>
        Met
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={leg("miss")}>
          <Glyph m="miss" />
        </span>
        Missed
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={leg("none")} />
        No attempts
      </span>
    </div>
  );
}

/** Running header + logo + title + score box. */
export function Masthead({
  g,
  who,
  kicker,
  title,
  report = "Postgame Report",
  win,
}: {
  g: GameMeta;
  who: string;
  kicker: string;
  title: string;
  report?: string;
  win?: WindowHead | null;
}) {
  return (
    <>
      <div className="ph">
        <div className="rpt">
          {win ? "KPI Dashboard" : report}{" "}
          <span className="yr">· {win ? win.line : g.line}</span>
        </div>
        <div className="who">{who}</div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: "2px 0 8px",
        }}
      >
        <img
          src={LOGO}
          alt="Portland Trail Blazers"
          style={{
            width: "0.38in",
            height: "0.38in",
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
        <div>
          <div
            style={{
              fontSize: 10,
              lineHeight: 1,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#6b6864",
              fontWeight: 700,
              marginBottom: 6,
              whiteSpace: "nowrap",
            }}
          >
            {kicker}
          </div>
          <div
            style={{
              fontFamily: DISP,
              fontWeight: 800,
              fontSize: 28,
              lineHeight: 0.9,
              textTransform: "uppercase",
            }}
          >
            {title}
            {win && (
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginLeft: 10,
                  whiteSpace: "nowrap",
                }}
              >
                · {win.name}
                {win.range && (
                  <span style={{ color: "#6b6864" }}> · {win.range}</span>
                )}
              </span>
            )}
          </div>
        </div>
        {win ? <WindowBox s={win.summary} /> : <ScoreBox g={g} />}
      </div>
    </>
  );
}

export function ScoreBox({ g }: { g: GameMeta }) {
  return (
    <div
      style={{
        marginLeft: "auto",
        display: "flex",
        gap: 8,
        alignItems: "stretch",
      }}
    >
      <div
        style={{
          border: "2px solid #000",
          padding: "4px 12px 5px",
          background: "#fff",
          whiteSpace: "nowrap",
        }}
      >
        <div
          style={{
            fontFamily: DISP,
            fontWeight: 800,
            fontSize: 20,
            lineHeight: 1,
            display: "flex",
            gap: 8,
            alignItems: "baseline",
          }}
        >
          <span>
            {g.us} {g.por}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#3a3a3a" }}>
            {g.opp} {g.oppScore}
          </span>
        </div>
        <div
          style={{
            fontSize: 10,
            color: "#3a3a3a",
            marginTop: 3,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {g.w} · {g.at} {g.oppName}
          {g.gp != null ? ` · Game ${g.gp}` : ""}
        </div>
      </div>
    </div>
  );
}

export function Footer({
  left = "Portland Trail Blazers · Postgame Packet",
  right,
}: {
  left?: string;
  right: ReactNode;
}) {
  return (
    <div className="pf">
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
}

export function Pending({ title, reason }: { title: string; reason?: ReactNode }) {
  return (
    <div className="kpi-pending">
      <div className="t">{title}</div>
      <div>{reason || "Not available yet."}</div>
    </div>
  );
}

/** The score box of a multi-game window: the record, the point differential, the games. */
export function WindowBox({ s }: { s: WindowSummary | null | undefined }) {
  if (!s) return <div style={{ marginLeft: "auto" }} />;
  const diff = s.pointDiff > 0 ? `+${s.pointDiff}` : `${s.pointDiff}`;
  return (
    <div
      style={{
        marginLeft: "auto",
        border: "2px solid #000",
        padding: "4px 12px 5px",
        background: "#fff",
        whiteSpace: "nowrap",
      }}
    >
      <div
        style={{
          fontFamily: DISP,
          fontWeight: 800,
          fontSize: 20,
          lineHeight: 1,
          display: "flex",
          gap: 8,
          alignItems: "baseline",
        }}
      >
        <span>
          {s.wins}–{s.losses}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#3a3a3a" }}>
          {diff}
        </span>
      </div>
      <div
        style={{
          fontSize: 10,
          color: "#3a3a3a",
          marginTop: 3,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        Record · {diff} pt diff · {s.games} games
      </div>
    </div>
  );
}

/** Full screen for casting and screenshots (hides the app's nav bar), as on the rebounding board. */
export function FullScreenButton({
  target,
  floating = false,
}: {
  target: RefObject<HTMLElement>;
  floating?: boolean;
}) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const change = () => setOn(document.fullscreenElement === target.current);
    document.addEventListener("fullscreenchange", change);
    return () => document.removeEventListener("fullscreenchange", change);
  }, [target]);
  const toggle = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else target.current?.requestFullscreen?.();
  };
  return (
    <button
      className={floating ? "kpi-fs kpi-fs-float" : "kpi-fs"}
      onClick={toggle}
      title={on ? "Exit full screen" : "Full screen"}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="square"
      >
        <path
          d={
            on
              ? "M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6"
              : "M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6"
          }
        />
      </svg>
      {on ? "Exit full screen" : "Full screen"}
    </button>
  );
}

/** True while the browser prints the page (Cmd-P on a screen page). The switch to true is flushed inside `beforeprint`,
 * before the browser lays the page out for print, so print-only markup (cards without links) is what gets printed. */
export function usePrinting() {
  const [printing, setPrinting] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia &&
      window.matchMedia("print").matches,
  );
  useEffect(() => {
    const on = () => flushSync(() => setPrinting(true));
    const off = () => setPrinting(false);
    window.addEventListener("beforeprint", on);
    window.addEventListener("afterprint", off);
    return () => {
      window.removeEventListener("beforeprint", on);
      window.removeEventListener("afterprint", off);
    };
  }, []);
  return printing;
}
